import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { API } from '../api';
import { useAuth } from './AuthContext';

const DataContext = createContext(null);

export const DataProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [users, setUsers] = useState([]);
  const [usersPagination, setUsersPagination] = useState(null);
  const [usersStats, setUsersStats] = useState(null);
  const [stats, setStats] = useState(null);
  const [audioDuration, setAudioDuration] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);

  const loadData = useCallback(async () => {
    if (!isAuthenticated) return;
    
    setLoading(true);
    try {
      const [usersRes, statsRes, audioDurationRes] = await Promise.all([
        API.users.getAll({ page: 1, limit: 5 }), // Initial load with limit 5
        API.stats.getOverview(),
        API.stats.getAudioDuration()
      ]);

      if (usersRes.success) {
        if (usersRes.data.data) {
          setUsers(usersRes.data.data);
          setUsersPagination(usersRes.data.pagination);
          setUsersStats(usersRes.data.stats);
        } else {
          setUsers(usersRes.data);
          setUsersPagination(null);
          setUsersStats(null);
        }
      }
      if (statsRes.success) setStats(statsRes.data);
      if (audioDurationRes.success) setAudioDuration(audioDurationRes.data);
      setDataLoaded(true);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const fetchUsers = useCallback(async (page = 1, limit = 5) => {
    setLoading(true);
    try {
      const result = await API.users.getAll({ page, limit });
      if (result.success) {
        if (result.data.data) {
          setUsers(result.data.data);
          setUsersPagination(result.data.pagination);
          setUsersStats(result.data.stats);
        } else {
          setUsers(result.data);
        }
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && !dataLoaded) {
      loadData();
    }
  }, [isAuthenticated, dataLoaded, loadData]);

  const updateUser = (updatedUser) => {
    setUsers(prevUsers => 
      prevUsers.map(user => user.id === updatedUser.id ? updatedUser : user)
    );
  };

  const refreshData = () => {
    setDataLoaded(false); // Trigger reload
  };

  return (
    <DataContext.Provider value={{ 
      users, 
      usersPagination,
      usersStats,
      stats,
      audioDuration,
      loading, 
      loadData, 
      fetchUsers,
      updateUser, 
      refreshData,
      setUsers 
    }}>
      {children}
    </DataContext.Provider>
  );
};

export const useData = () => useContext(DataContext);
